import React from "react";
import { Box, ListSubheader, Typography, TextField, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import { useRouter } from 'next/router';
import * as gtag from "../../../../lib/google/gtag";
import * as send from '../../../../lib/analytics/send';
import { searchFund } from "../../../utils/helpers";
import { createListboxComponent, StyledPopper, LISTBOX_PADDING_VALUE } from '../SearchCommon/VirtualizedListbox';

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING_VALUE,
  };

  if (dataSet && dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader
        key={dataSet.key}
        component="div"
        style={inlineStyle}
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'grey.500',
          py: 0,
          lineHeight: '48px',
          height: '48px !important',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        {dataSet.group}
      </ListSubheader>
    );
  }

  if (React.isValidElement(dataSet)) {
    return React.cloneElement(dataSet, { style: inlineStyle });
  }

  return null;
}

const ListboxComponent = createListboxComponent(renderRow);

const SearchFund = ({ setShowSearch, removebackdrop, placeholder, dataEventFrom, variant = 'dark' }) => {
  const isLight = variant === 'light';
  const [value, setValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const router = useRouter();
  const loading = open && options.length === 0;

  const handleSearch = (event, search_term) => {
    router.push(`/funds/${search_term}`);
    send.uevent(event, `fund-search-${dataEventFrom} ${search_term}`);
    gtag.event({
      action: "fund_search",
      search_term: search_term,
    });
  };

  React.useEffect(() => {
    let active = true;
    if (!loading) return undefined;

    (async () => {
      const fundSearch_jsonData = await searchFund({
        url: `/api/fund-search`,
        searchString: "",
      }) || [];
      if (active && fundSearch_jsonData) {
        setOptions([...fundSearch_jsonData]);
      }
    })();

    return () => { active = false; };
  }, [loading]);

  React.useEffect(() => {
    if (!open) setOptions([]);
  }, [open]);

  const textColor = isLight ? 'text.primary' : '#fff';
  const placeholderColor = isLight ? 'grey.500' : 'rgba(255,255,255,0.55)';
  const iconColor = isLight ? 'grey.400' : 'rgba(255,255,255,0.7)';

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Autocomplete
        type="search"
        data-event-name="Search"
        data-event-category={dataEventFrom}
        data-event-label="Fund Search"
        id="search-funds"
        autoHighlight
        disableListWrap
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-endAdornment svg': { color: iconColor },
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => { setOpen(false); setShowSearch(false); }}
        onChange={(event, newValue) => {
          setValue(newValue);
          if (router && newValue && newValue.f) {
            handleSearch(event, newValue.f);
          }
        }}
        isOptionEqualToValue={(option, value) => option.n === value.n}
        filterOptions={(options, { inputValue }) => {
          if (inputValue === '') return [];
          return options.filter((item) => item.n.toLowerCase().includes(inputValue.toLowerCase()));
        }}
        options={options}
        loading={loading}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          if (option.inputValue) return option.inputValue;
          return '';
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        freeSolo
        noOptionsText={
          <Typography variant="body2" sx={{ color: 'grey.500', py: 0.5 }}>
            No matching funds found
          </Typography>
        }

        renderOption={(props, option) => {
          const { key, ...otherProps } = props;

          if (!option?.n) return null;

          return (
            <Box
              key={key}
              component="li"
              {...otherProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: '16px !important',
                cursor: 'pointer',
                transition: 'background-color 150ms ease',
                '&:hover, &.Mui-focused': {
                  bgcolor: 'grey.50',
                },
                '&[aria-selected="true"]': {
                  bgcolor: '#eff6ff !important',
                  borderLeft: '2px solid',
                  borderColor: 'secondary.main',
                  pl: '14px !important',
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  color: 'text.primary',
                  fontWeight: 400,
                  fontSize: '0.85rem',
                  minWidth: 0,
                }}
              >
                {option.n.toUpperCase()}
              </Typography>
            </Box>
          );
        }}

        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || "Search funds..."}
            size="small"
            variant="filled"
            hiddenLabel
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5, mr: -0.5 }}>
                    <SearchIcon sx={{ color: iconColor, fontSize: '1.25rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} sx={{ color: iconColor, mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
            sx={{
              width: { md: '35.4ch', xs: '28ch' },
              animationDuration: "10ms",
              transition: "width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              '& .MuiFilledInput-root': {
                py: '6px !important',
                background: 'transparent',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: textColor,
                '&:hover': { background: 'transparent' },
                '&.Mui-focused': { background: 'transparent' },
                '&::before, &::after': { display: 'none' },
              },
              '& .MuiFilledInput-input': {
                color: textColor,
                '&::placeholder': { color: placeholderColor, opacity: 1 },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default SearchFund;
